import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import NiceModal from '@ebay/nice-modal-react';
import { ConfirmDialog } from '@packages/ui/composites';
import type { ConfirmDialogResult } from '@packages/ui/composites';
import { resetScrollLock } from '@packages/ui/internals';
import ToastComponent from '@/components/toast';
import AuthService from '@/services/authService';
import useCascadeRedirect, { OVERVIEW_PATHS, type CascadeResult } from '@/hooks/custom-hooks/useCascadeRedirect';
import {
  REDIRECT_PERMISSION_MESSAGE,
  RELOAD_PERMISSION_MESSAGE,
  LOGOUT_NOTIFICATION_MESSAGE,
  AUTH_FAILURE_MESSAGE,
} from '@/constants/common';

/**
 * Encapsulates permission-changed / tenant-deleted / logout event listeners.
 * Shows AlertDialogs imperatively via NiceModal — no JSX return needed.
 */
export function usePermissionGuard({
  isLogoutModalOpen,
  closeLogoutModal,
}: {
  isLogoutModalOpen: boolean;
  closeLogoutModal: () => void;
}): void {
  const navigate = useNavigate();
  const authService = new AuthService();
  const { evaluateFallback } = useCascadeRedirect();

  const PERM_RETRY_KEY = 'permissionRetryCount';
  const permissionRetryCountRef = useRef(Number(sessionStorage.getItem(PERM_RETRY_KEY)) || 0);

  const evaluateFallbackRef = useRef(evaluateFallback);
  evaluateFallbackRef.current = evaluateFallback;
  const cascadeBusyRef = useRef(false);

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const authServiceRef = useRef(authService);
  authServiceRef.current = authService;

  useEffect(() => {
    const getPermissionMessage = (status: string) =>
      status === 'RELOAD'
        ? RELOAD_PERMISSION_MESSAGE
        : status === 'AUTH_FAILURE'
        ? AUTH_FAILURE_MESSAGE
        : REDIRECT_PERMISSION_MESSAGE;

    const handlePermissionAction = (status: string) => {
      window.dispatchEvent(new CustomEvent('PERMISSION_HANDLED'));
      if (status === 'AUTH_FAILURE') {
        permissionRetryCountRef.current = 0;
        sessionStorage.removeItem(PERM_RETRY_KEY);
        resetScrollLock();
        void authServiceRef.current.logout();
      } else if (status === 'SIGN_IN') {
        navigateRef.current(`/check-user${window.location.hash}`);
      } else if (status === 'REFRESH') {
        resetScrollLock();
        window.location.href = `/overview${window.location.hash}`;
      } else {
        resetScrollLock();
        navigateRef.current(0);
      }
    };

    const showPermissionAlert = (status: string) => {
      NiceModal.show(ConfirmDialog, {
        title: getPermissionMessage(status),
        variant: 'single',
        semanticId: 'permissionAlert',
      } as any).then(() => {
        handlePermissionAction(status);
      });
    };

    const showCascadeAlert = (result: CascadeResult) => {
      NiceModal.show(ConfirmDialog, {
        title: result.message ?? '',
        variant: 'single',
        actionLabel: result.confirmTitle ?? 'Ok',
        semanticId: 'cascadeAlert',
      } as any).then(() => {
        cascadeBusyRef.current = false;
        window.dispatchEvent(new CustomEvent('PERMISSION_HANDLED'));
        result.execute();
      });
    };

    const handlePermissionChanged = (evt: any) => {
      const detail = evt.detail;
      const isSwitchedContext = window.location.hash.includes('cid=');

      if (detail === 'REFRESH' && isSwitchedContext) {
        if (cascadeBusyRef.current) return;
        cascadeBusyRef.current = true;
        evaluateFallbackRef.current('PERMISSION_CHANGED').then((result) => {
          if (result === null) {
            cascadeBusyRef.current = false;
            window.dispatchEvent(new CustomEvent('PERMISSION_HANDLED'));
            resetScrollLock();
            window.location.href = `/${window.location.hash}`;
          } else {
            showCascadeAlert(result);
          }
        });
      } else {
        if (
          detail === 'REFRESH' &&
          !isSwitchedContext &&
          OVERVIEW_PATHS.includes(window.location.pathname)
        ) {
          permissionRetryCountRef.current += 1;
          sessionStorage.setItem(PERM_RETRY_KEY, String(permissionRetryCountRef.current));
        }

        let status = detail;
        if (detail === 'REFRESH' && permissionRetryCountRef.current >= 3) {
          toast.error(
            <ToastComponent
              label="Error"
              message="Unable to authorize your session. You will be logged out."
            />,
            { toastId: 'auth-failure', autoClose: 5000 }
          );
          status = 'AUTH_FAILURE';
        }
        showPermissionAlert(status);
      }
    };

    const handleTenantDeleted = () => {
      evaluateFallbackRef.current('TENANT_DELETED').then((result) => {
        if (result != null) {
          showCascadeAlert(result);
        }
      });
    };

    window.addEventListener('PERMISSION_CHANGED', handlePermissionChanged);
    window.addEventListener('TENANT_DELETED', handleTenantDeleted);

    return () => {
      window.removeEventListener('PERMISSION_CHANGED', handlePermissionChanged);
      window.removeEventListener('TENANT_DELETED', handleTenantDeleted);
    };
  }, []);

  // Cross-tab logout alert
  useEffect(() => {
    if (!isLogoutModalOpen) return;

    NiceModal.show(ConfirmDialog, {
      title: LOGOUT_NOTIFICATION_MESSAGE,
      variant: 'single',
      semanticId: 'logoutAlert',
    } as any).then(() => {
      closeLogoutModal();
      resetScrollLock();
      window.location.reload();
    });
  }, [isLogoutModalOpen]);
}
